import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import '../styles/global.css';
import '../styles/RegisterPage.css';
import { toFriendlyMessage } from '../utils/errorUtils';
import { useAuth } from '../components/AuthContext';
import { FaGoogle, FaFacebook } from 'react-icons/fa';
import Toast from '../components/Toast';
import ConfirmationDialog from '../components/ConfirmationDialog';
import {
  TextField, Button, InputAdornment, IconButton, MenuItem, Box, Typography, Grid, Divider, ToggleButtonGroup, ToggleButton
} from '@mui/material';
import { Visibility, VisibilityOff, Person, Email, Lock, Cake } from '@mui/icons-material';
import SchoolIcon from '@mui/icons-material/School';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import { useTranslation } from 'react-i18next';

/* Floating decoration bubble */
const FloatingShape = ({ size, top, left, delay, duration, color }) => (
  <Box sx={{
    position: 'absolute', width: size, height: size, borderRadius: '50%',
    background: color, opacity: 0.15, top, left,
    animation: `regFloat ${duration}s ${delay}s ease-in-out infinite`,
    pointerEvents: 'none'
  }} />
);

export default function RegisterPage() {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [dob, setDob] = useState('');
  const [gender, setGender] = useState('');
  const [bio, setBio] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [selectedRole, setSelectedRole] = useState('student');
  const [pwdHintOpen, setPwdHintOpen] = useState(false);
  const [comingSoonOpen, setComingSoonOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  // Check for redirect message from protected route
  useEffect(() => {
    if (location.state?.message) {
      setMessage(location.state.message);
    }
  }, [location]);

  const emailRegex = /[^@\s]+@[^@\s]+\.[^@\s]+/;

  // Local funny bio suggestions (<= 150 chars), language-aware
  const bioSuggestions = React.useMemo(() => {
    const en = [
      'Professional coffee drinker \u2615, amateur adult.',
      'Currently training for Netflix marathons \ud83c\udf7f.',
      'Living proof that Wi-Fi is essential to survival.',
      'Part-time comedian, full-time snack enthusiast.',
      'Fluent in emoji, sarcasm, and Ctrl+Z.',
      'Powered by caffeine and chaotic good.',
      'Just here to avoid doing the dishes.',
      'I put the "pro" in procrastinate.',
      'Running on 90% memes, 10% ambition.',
      'Becoming who my dog thinks I am \ud83d\udc36.',
      'Dreaming of vacations I can\'t afford \u2708\ufe0f.',
      'My hobbies include pressing "Remind me later."',
      'Currently accepting snack donations \ud83c\udf69.',
      'Professional overthinker since birth.',
      'I hit snooze more than I hit the gym.',
      'Socially awkward, digitally available \ud83d\udcbb.',
      'Trying to be an adult, failing gracefully.',
      'Collecting bad puns and good vibes.',
      'Powered by playlists and pizza \ud83c\udf55.',
      'Spending my free time doing nothing productively.'
    ];

    const vi = [
      'Chuy\u00ean gia u\u1ed1ng c\u00e0 ph\u00ea \u2615, nghi\u1ec7p d\u01b0 l\u00e0m ng\u01b0\u1eddi l\u1edbn.',
      '\u0110ang luy\u1ec7n marathon Netflix \ud83c\udf7f.',
      'B\u1eb1ng ch\u1ee9ng s\u1ed1ng r\u1eb1ng Wi-Fi l\u00e0 nhu y\u1ebfu ph\u1ea9m.',
      'H\u00e0i h\u01b0\u1edbc b\u00e1n th\u1eddi gian, \u0103n v\u1eb7t to\u00e0n th\u1eddi gian.',
      'Th\u00f4ng th\u1ea1o emoji, m\u1ec9a mai v\u00e0 Ctrl+Z.',
      'Ch\u1ea1y b\u1eb1ng caffeine v\u00e0 n\u0103ng l\u01b0\u1ee3ng t\u00edch c\u1ef1c.',
      '\u1ede \u0111\u00e2y ch\u1ec9 \u0111\u1ec3 n\u00e9 r\u1eeda b\u00e1t.',
      'T\u00f4i l\u00e0 "pro" trong kho\u1ea3n tr\u00ec ho\u00e3n.',
      '90% meme, 10% tham v\u1ecdng.',
      'Tr\u1edf th\u00e0nh ng\u01b0\u1eddi m\u00e0 ch\u00f3 t\u00f4i ngh\u0129 t\u00f4i l\u00e0 \ud83d\udc36.',
      'M\u01a1 v\u1ec1 k\u1ef3 ngh\u1ec9 xa x\u1ec9 \u2708\ufe0f m\u00e0 v\u00ed kh\u00f4ng cho ph\u00e9p.',
      'S\u1edf th\u00edch: b\u1ea5m "Nh\u1eafc t\u00f4i sau" v\u00f4 t\u1eadn.',
      'Lu\u00f4n s\u1eb5n s\u00e0ng nh\u1eadn \u0111\u1ed3 \u0103n v\u1eb7t \ud83c\udf69.',
      'Chuy\u00ean gia suy ngh\u0129 qu\u00e1 nhi\u1ec1u t\u1eeb b\u00e9.',
      'B\u1ea5m b\u00e1o th\u1ee9c nhi\u1ec1u h\u01a1n b\u1ea5m th\u1ebb t\u1eadp gym.',
      'H\u01a1i ng\u1ea1i giao ti\u1ebfp, nh\u01b0ng online th\u00ec lu\u00f4n c\u00f3 \ud83d\udcbb.',
      'C\u1ed1 g\u1eafng l\u00e0m ng\u01b0\u1eddi l\u1edbn, nh\u01b0ng v\u1eabn ng\u00e3 ng\u1eeda.',
      'S\u01b0u t\u1ea7m tr\u00f2 \u0111\u00f9a d\u1edf v\u00e0 n\u0103ng l\u01b0\u1ee3ng t\u00edch c\u1ef1c.',
      'S\u1ed1ng nh\u1edd playlist v\u00e0 pizza \ud83c\udf55.',
      'D\u00e0nh th\u1eddi gian r\u1ea3nh \u0111\u1ec3\u2026 r\u1ea3nh th\u00eam n\u1eefa.'
    ];
    const lang = (i18n?.resolvedLanguage || i18n?.language || (typeof navigator !== 'undefined' ? (navigator.language || 'en') : 'en'));
    const l = String(lang).toLowerCase();
    return l.startsWith('vi') ? vi : en;
  }, [i18n?.language, i18n?.resolvedLanguage]);
  const getRandomBio = () => {
    const list = bioSuggestions.filter(s => typeof s === 'string' && s.length <= 150);
    if (!list.length) return '';
    return list[Math.floor(Math.random() * list.length)];
  };
  const handleGenerateBio = () => {
    const suggestion = getRandomBio();
    if (suggestion) setBio(suggestion.slice(0, 150));
  };

  const passwordScore = (pwd) => {
    let score = 0;
    if (pwd.length >= 8) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[a-z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;
    return Math.min(score, 4); // 0-4
  };

  const strengthLabel = [
    { text: t('register.strength.veryWeak'), color: '#e53e3e' },
    { text: t('register.strength.weak'), color: '#f56565' },
    { text: t('register.strength.medium'), color: '#d69e2e' },
    { text: t('register.strength.good'), color: '#38a169' },
    { text: t('register.strength.strong'), color: '#2f855a' }
  ];

  const validate = () => {
    const errs = {};
    if (!username.trim()) errs.username = t('register.errors.usernameRequired');
    if (!emailRegex.test(email)) errs.email = t('register.errors.invalidEmail');
    if (password.length < 8) errs.password = t('register.errors.passwordMin8');
    if (password !== confirmPassword) errs.confirmPassword = t('register.errors.passwordMismatch');
    // DOB validation: optional, but if provided must be at least 6 years old and not today/future (skip for parent)
    if (dob && selectedRole !== 'parent') {
      const today = new Date(); today.setHours(0,0,0,0);
      const birth = new Date(dob); birth.setHours(0,0,0,0);
      // compute age
      let age = today.getFullYear() - birth.getFullYear();
      const m = today.getMonth() - birth.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
      if (birth >= today || age < 6) {
        errs.dob = t('register.errors.dobInvalid');
      }
    }
    return errs;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length) {
      const msg = t('register.errors.checkRequired');
      setError(msg);
      setMessage(msg);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, email, password, dateOfBirth: dob || undefined, gender: gender || undefined, bio: (bio || '').trim().slice(0, 200) || undefined, role: selectedRole })
      });

      const contentType = response.headers.get('content-type');

      if (!response.ok) {
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          throw new Error(errorData.error || t('register.registerFailed'));
        } else {
          const errorText = await response.text();
          console.error('Non-JSON error response:', errorText);
          throw new Error(t('auth.serverError'));
        }
      }

      if (!contentType || !contentType.includes('application/json')) {
        throw new Error(t('auth.invalidResponse'));
      }

      const data = await response.json();

      if (data?.token && data?.user?.status === 'active') {
        login(data.user, data.token);
        if (location.state?.from) {
          navigate(location.state.from);
        } else {
          navigate('/');
        }
      } else {
        const msg = data?.message || t('register.pendingApproval');
        setMessage(msg);
        // Clear form values after successful submission
        setUsername('');
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setDob('');
        setGender('');
        setBio('');
        setShowPassword(false);
        setShowConfirm(false);
        setFieldErrors({});
        setPwdHintOpen(false);
      }
    } catch (err) {
      console.error('Registration error:', err);
      const friendly = toFriendlyMessage(err.message || String(err));
      setError(friendly);
      setMessage(friendly);
    } finally {
      setLoading(false);
    }
  };

  const score = passwordScore(password);
  const strength = password.length
    ? (strengthLabel[score] || strengthLabel[0])
    : { text: t('register.enterPassword'), color: '#9ca3af' };

  /* Shared input style */
  const inputPropsSx = { borderRadius: '14px', fontSize: '1rem' };

  return (
    <>
      <style>{`
        @keyframes regFloat {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-12px); }
        }
        @keyframes regGradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes regSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Full-screen animated gradient background */}
      <Box sx={{
        minHeight: 'calc(100vh - 60px)',
        mt: '-1.25em',
        background: 'linear-gradient(135deg, #22c55e 0%, #3b82f6 35%, #8b5cf6 70%, #ec4899 100%)',
        backgroundSize: '300% 300%',
        animation: 'regGradientShift 10s ease infinite',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        position: 'relative', overflow: 'hidden',
        py: { xs: 3, md: 5 }, px: 2
      }}>
        {/* Floating background shapes */}
        <FloatingShape size={200} top="2%" left="-3%" delay={0} duration={7} color="#fff" />
        <FloatingShape size={120} top="60%" left="5%" delay={1.2} duration={5.5} color="#22c55e" />
        <FloatingShape size={90} top="10%" left="80%" delay={0.6} duration={6} color="#8b5cf6" />
        <FloatingShape size={150} top="70%" left="78%" delay={2} duration={6.5} color="#fff" />
        <FloatingShape size={70} top="35%" left="92%" delay={1} duration={5} color="#ec4899" />
        <FloatingShape size={100} top="85%" left="40%" delay={0.3} duration={7.5} color="#3b82f6" />

        {/* Main card */}
        <Box sx={{
          width: '100%', maxWidth: 580,
          borderRadius: '28px',
          bgcolor: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(24px)',
          boxShadow: '0 24px 80px rgba(0,0,0,0.18)',
          position: 'relative', zIndex: 1,
          overflow: 'hidden'
        }}>

          {/* ===== Fun header banner inside the card ===== */}
          <Box sx={{
            background: 'linear-gradient(135deg, #22c55e 0%, #3b82f6 50%, #8b5cf6 100%)',
            px: { xs: 3, sm: 4 }, pt: { xs: 4, sm: 5 }, pb: { xs: 3, sm: 4 },
            textAlign: 'center', position: 'relative', overflow: 'hidden'
          }}>
            {/* Decorative circles in banner */}
            <Box sx={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.1)' }} />
            <Box sx={{ position: 'absolute', bottom: -20, left: -20, width: 80, height: 80, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.08)' }} />

            {/* Emoji ring */}
            <Box sx={{
              display: 'inline-flex', position: 'relative',
              width: { xs: 100, sm: 120 }, height: { xs: 100, sm: 120 },
              alignItems: 'center', justifyContent: 'center', mb: 2
            }}>
              {/* Spinning ring of dots */}
              <Box sx={{
                position: 'absolute', inset: 0, borderRadius: '50%',
                border: '3px dashed rgba(255,255,255,0.3)',
                animation: 'regSpin 20s linear infinite'
              }} />
              {/* Central emoji */}
              <Box sx={{
                fontSize: { xs: '3.5rem', sm: '4.2rem' }, lineHeight: 1,
                animation: 'regFloat 3s ease-in-out infinite',
                filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.2))'
              }}>
                🎓
              </Box>
              {/* Corner emojis */}
              <Box sx={{ position: 'absolute', top: -4, left: -4, fontSize: '1.4rem', animation: 'regFloat 4s 0.2s ease-in-out infinite' }}>✏️</Box>
              <Box sx={{ position: 'absolute', top: -4, right: -4, fontSize: '1.4rem', animation: 'regFloat 3.5s 0.8s ease-in-out infinite' }}>⭐</Box>
              <Box sx={{ position: 'absolute', bottom: -4, left: -4, fontSize: '1.4rem', animation: 'regFloat 4.5s 0.5s ease-in-out infinite' }}>🎨</Box>
              <Box sx={{ position: 'absolute', bottom: -4, right: -4, fontSize: '1.4rem', animation: 'regFloat 3.8s 1s ease-in-out infinite' }}>🚀</Box>
            </Box>

            <Typography variant="h4" fontWeight={900}
              sx={{ color: '#fff', textShadow: '0 2px 12px rgba(0,0,0,0.15)',
                fontSize: { xs: '1.7rem', sm: '2.1rem' }, letterSpacing: 0.5 }}>
              {t('register.title')}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.9)', mt: 0.5, fontWeight: 500,
              fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
              {t('register.letsStart')}
            </Typography>
          </Box>

          {/* ===== Form area ===== */}
          <Box sx={{ px: { xs: 3, sm: 4 }, py: { xs: 3, sm: 4 } }}>

            {/* Info message */}
            {message && !error && (
              <Box sx={{
                mb: 2.5, p: 2, borderRadius: '14px',
                bgcolor: '#ecfeff', color: '#0e7490',
                border: '1px solid #a5f3fc', fontSize: '0.95rem'
              }} role="status" aria-live="polite">
                {message}
              </Box>
            )}

            <Box component="form" onSubmit={handleRegister} noValidate>
              {/* Role picker */}
              <Box sx={{ mb: 2.5, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                  {t('register.selectRole', 'Bạn là ai?')}
                </Typography>
                <ToggleButtonGroup
                  value={selectedRole}
                  exclusive
                  onChange={(_, val) => { if (val) setSelectedRole(val); }}
                  sx={{ width: '100%', '& .MuiToggleButton-root': { flex: 1, borderRadius: '14px !important', py: 1.5, textTransform: 'none', fontWeight: 700, fontSize: '1rem', border: '2px solid', borderColor: 'rgba(59,130,246,0.2)', '&.Mui-selected': { background: 'linear-gradient(135deg, #22c55e, #3b82f6)', color: '#fff', borderColor: 'transparent', '&:hover': { background: 'linear-gradient(135deg, #16a34a, #2563eb)' } } } }}
                >
                  <ToggleButton value="student" aria-label="Student">
                    <SchoolIcon sx={{ mr: 1, fontSize: 22 }} />
                    {t('register.roleStudent', 'Học sinh')}
                  </ToggleButton>
                  <ToggleButton value="parent" aria-label="Parent">
                    <FamilyRestroomIcon sx={{ mr: 1, fontSize: 22 }} />
                    {t('register.roleParent', 'Phụ huynh')}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* Username + Email side by side on desktop */}
              <Grid container spacing={2} sx={{ mb: 0.5 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="username"
                    label={t('register.username')}
                    value={username}
                    onChange={(e) => { setUsername(e.target.value); setFieldErrors(prev => ({ ...prev, username: e.target.value.trim() ? '' : t('register.errors.usernameRequired') })); }}
                    onBlur={(e) => setFieldErrors(prev => ({ ...prev, username: e.target.value.trim() ? '' : t('register.errors.usernameRequired') }))}
                    required disabled={loading}
                    placeholder={t('register.usernamePlaceholder')}
                    error={!!fieldErrors.username}
                    helperText={fieldErrors.username || ''}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Person sx={{ color: '#22c55e', fontSize: 22 }} /></InputAdornment>,
                      sx: inputPropsSx
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth type="email" id="email"
                    label={t('auth.email')}
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setFieldErrors(prev => ({ ...prev, email: /[^@\s]+@[^@\s]+\.[^@\s]+/.test(e.target.value) ? '' : t('register.errors.invalidEmail') })); }}
                    onBlur={(e) => setFieldErrors(prev => ({ ...prev, email: /[^@\s]+@[^@\s]+\.[^@\s]+/.test(e.target.value) ? '' : t('register.errors.invalidEmail') }))}
                    required disabled={loading}
                    placeholder={t('register.emailPlaceholder')}
                    error={!!fieldErrors.email}
                    helperText={fieldErrors.email || ''}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Email sx={{ color: '#3b82f6', fontSize: 22 }} /></InputAdornment>,
                      sx: inputPropsSx
                    }}
                  />
                </Grid>
              </Grid>

              {/* Password + Confirm side by side */}
              <Grid container spacing={2} sx={{ mb: 0.5 }}>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <button type="button" onClick={() => setPwdHintOpen(v => !v)} aria-label={t('register.passwordHintAria')}
                        style={{
                          background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                          color: '#fff', border: 'none', width: 20, height: 20,
                          borderRadius: '50%', display: 'inline-flex', alignItems: 'center',
                          justifyContent: 'center', fontWeight: 700, fontSize: 12, cursor: 'pointer'
                        }}>?</button>
                      {pwdHintOpen && (
                        <Typography variant="caption" sx={{
                          bgcolor: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe',
                          borderRadius: '8px', px: 1, py: 0.25, fontSize: '0.78rem'
                        }}>
                          {t('register.passwordHint')}
                        </Typography>
                      )}
                    </Box>
                    <TextField
                      fullWidth id="password"
                      label={t('register.password')}
                      name="new-password" autoComplete="new-password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); setFieldErrors(prev => ({ ...prev, password: e.target.value.length >= 8 ? '' : prev.password })); }}
                      onBlur={(e) => setFieldErrors(prev => ({ ...prev, password: e.target.value.length >= 8 ? '' : t('register.errors.passwordMin8') }))}
                      required disabled={loading}
                      inputProps={{ minLength: 8 }}
                      placeholder="••••••••"
                      error={!!fieldErrors.password}
                      helperText={fieldErrors.password || ''}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><Lock sx={{ color: '#8b5cf6', fontSize: 22 }} /></InputAdornment>,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton aria-label={showPassword ? t('profile.security.hidePassword') : t('profile.security.showPassword')}
                              onClick={() => setShowPassword(s => !s)} edge="end" size="small">
                              {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        ),
                        sx: inputPropsSx
                      }}
                    />
                    {/* Strength bar */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
                      <Box sx={{ height: 6, borderRadius: 99, width: 100, bgcolor: '#e5e7eb', overflow: 'hidden' }}>
                        <Box sx={{
                          height: '100%', borderRadius: 99,
                          width: `${password.length ? Math.max((score / 4) * 100, 10) : 0}%`,
                          bgcolor: strength.color, transition: 'width 0.3s, background-color 0.3s'
                        }} />
                      </Box>
                      <Typography variant="caption" sx={{ color: strength.color, fontWeight: 600, fontSize: '0.75rem' }}>
                        {strength.text}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mt: { xs: 0, sm: 3.2 } }}>
                    <TextField
                      fullWidth id="confirmPassword"
                      label={t('register.confirmPassword')}
                      name="confirm-password" autoComplete="new-password"
                      type={showConfirm ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => { setConfirmPassword(e.target.value); setFieldErrors(prev => ({ ...prev, confirmPassword: e.target.value === password ? '' : t('register.errors.passwordMismatch') })); }}
                      onBlur={(e) => setFieldErrors(prev => ({ ...prev, confirmPassword: e.target.value === password ? '' : t('register.errors.passwordMismatch') }))}
                      required disabled={loading}
                      inputProps={{ minLength: 8 }}
                      placeholder={t('register.confirmPlaceholder')}
                      error={!!fieldErrors.confirmPassword}
                      helperText={fieldErrors.confirmPassword || ''}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><Lock sx={{ color: '#8b5cf6', fontSize: 22 }} /></InputAdornment>,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton aria-label={showConfirm ? t('profile.security.hidePassword') : t('profile.security.showPassword')}
                              onClick={() => setShowConfirm(s => !s)} edge="end" size="small">
                              {showConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        ),
                        sx: inputPropsSx
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>

              {/* DOB + Gender side by side (DOB hidden for parent) */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {selectedRole !== 'parent' && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth type="date" id="dob"
                    label={t('register.dobOptional')}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Cake sx={{ color: '#f59e0b', fontSize: 22 }} /></InputAdornment>,
                      sx: inputPropsSx
                    }}
                    inputProps={{
                      max: (() => {
                        const d = new Date(); d.setFullYear(d.getFullYear() - 6);
                        return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
                      })()
                    }}
                    value={dob}
                    onChange={(e) => {
                      setDob(e.target.value);
                      const v = e.target.value; let err = '';
                      if (v) {
                        const today = new Date(); today.setHours(0,0,0,0);
                        const birth = new Date(v); birth.setHours(0,0,0,0);
                        let age = today.getFullYear() - birth.getFullYear();
                        const m = today.getMonth() - birth.getMonth();
                        if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
                        if (birth >= today || age < 6) err = t('register.errors.dobInvalid');
                      }
                      setFieldErrors(prev => ({ ...prev, dob: err }));
                    }}
                    disabled={loading} error={!!fieldErrors.dob} helperText={fieldErrors.dob || ''}
                  />
                </Grid>
                )}
                <Grid item xs={12} sm={selectedRole === 'parent' ? 12 : 6}>
                  <TextField
                    fullWidth id="gender" label={t('register.genderOptional')}
                    select value={gender} onChange={(e) => setGender(e.target.value)} disabled={loading}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ sx: inputPropsSx }}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (sel) => sel ? (sel === 'male' ? t('register.genderMale') : sel === 'female' ? t('register.genderFemale') : sel === 'other' ? t('register.genderOther') : sel) : t('register.genderPlaceholder')
                    }}
                  >
                    <MenuItem value="">{t('register.genderPlaceholder')}</MenuItem>
                    <MenuItem value="male">{t('register.genderMale')}</MenuItem>
                    <MenuItem value="female">{t('register.genderFemale')}</MenuItem>
                    <MenuItem value="other">{t('register.genderOther')}</MenuItem>
                  </TextField>
                </Grid>
              </Grid>

              {/* Bio */}
              <TextField
                fullWidth id="bio" label={t('register.bioOptional')}
                value={bio} onChange={(e) => setBio(e.target.value.slice(0, 200))}
                disabled={loading}
                placeholder={t('profile.bio.placeholder', 'Tell something fun about yourself\u2026')}
                multiline minRows={2} inputProps={{ maxLength: 200 }}
                helperText={`${(bio || '').length}/200`}
                sx={{ mb: 2.5 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                      <IconButton onClick={handleGenerateBio} disabled={loading}
                        title={t('profile.bio.generateTooltip', 'Generate a funny bio')}
                        aria-label={t('profile.bio.generateTooltip', 'Generate a funny bio')}
                        edge="end" size="small">
                        <span role="img" aria-hidden="true" style={{ fontSize: 18 }}>🎲</span>
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: { borderRadius: '14px', fontSize: '0.95rem' }
                }}
              />

              {/* Register button */}
              <Button type="submit" fullWidth disabled={loading}
                sx={{
                  height: 54, borderRadius: '14px', fontSize: '1.1rem', fontWeight: 700,
                  background: 'linear-gradient(135deg, #22c55e, #3b82f6)',
                  color: '#fff', textTransform: 'none',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  boxShadow: '0 4px 20px rgba(34,197,94,0.35)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #16a34a, #2563eb)',
                    transform: 'translateY(-3px)',
                    boxShadow: '0 10px 30px rgba(34,197,94,0.45)'
                  },
                  '&.Mui-disabled': {
                    background: 'linear-gradient(135deg, #86efac, #93c5fd)',
                    color: 'rgba(255,255,255,0.7)'
                  }
                }}
              >
                {loading ? t('register.submit.registering') : t('register.submit.register')}
              </Button>

              <Divider sx={{ my: 2.5 }}>{t('common.or')}</Divider>

              {/* Social buttons */}
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <Button fullWidth variant="outlined"
                    startIcon={<FaGoogle aria-hidden="true" />}
                    onClick={() => setComingSoonOpen(true)}
                    sx={{ borderRadius: '14px', textTransform: 'none', height: 46, fontSize: '0.9rem' }}>
                    Google
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button fullWidth variant="outlined"
                    startIcon={<FaFacebook aria-hidden="true" />}
                    onClick={() => setComingSoonOpen(true)}
                    sx={{ borderRadius: '14px', textTransform: 'none', height: 46, fontSize: '0.9rem' }}>
                    Facebook
                  </Button>
                </Grid>
              </Grid>
            </Box>

            <ConfirmationDialog
              isOpen={comingSoonOpen}
              title={t('register.dialog.comingSoonTitle')}
              message={t('register.dialog.comingSoonMessage')}
              cancelText={t('register.dialog.close')}
              onCancel={() => setComingSoonOpen(false)}
            />

            {/* Login link */}
            <Box sx={{
              mt: 3, p: 2, borderRadius: '14px',
              background: 'linear-gradient(135deg, rgba(34,197,94,0.08), rgba(59,130,246,0.08))',
              border: '2px dashed rgba(59,130,246,0.25)', textAlign: 'center'
            }}>
              <Typography variant="body2" sx={{ fontSize: '0.95rem' }}>
                {t('register.haveAccount')}{' '}
                <Link to="/login" style={{ color: '#3b82f6', fontWeight: 700, textDecoration: 'none' }}>
                  {t('register.login')}
                </Link>
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Toast
        message={error || message}
        type={error ? 'error' : 'success'}
        duration={4000}
        onClose={() => { setMessage(''); setError(null); }}
        silent
      />
    </>
  );
}
